import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Building2, Mail, Crown, User, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useOrganization } from '@/hooks/useOrganization';
import { acceptInvitationSupabase } from '@/services/organizationService';
import { toast } from '@/hooks/use-toast';
import {
  loginFadeIn,
  loginFormStagger,
  loginFormItem,
  loginLogo,
  loginButton
} from '@/lib/animations';
import { useThemeLogos } from "@/hooks/useThemeLogos";
import { AuthRightPanel } from '@/components/auth/AuthRightPanel';

// Delay (in ms) to show the user the invitation accepted message, then take them to set their password
const setPasswordTimeout = 4000;

const InvitationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshOrganizations, switchOrganization } = useOrganization();
  const { favicon: hobbesFavicon } = useThemeLogos();

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [userAlreadyExists, setUserAlreadyExists] = useState(false);
  const [invitationData, setInvitationData] = useState<{
    email: string;
    orgId: string;
    role: string;
    accessToken: string;
  } | null>(null);

  useEffect(() => {
    const validateInvitationToken = async () => {
      console.log('🔍 [InvitationPage] Validating invitation token');
      
      try {
        // Parse invitation token from query parameters
        const invitationToken = searchParams.get('token');
        
        if (!invitationToken) {
          console.log('❌ [InvitationPage] No invitation token found');
          setError('Invalid invitation link - missing token');
          setLoading(false);
          return;
        }
        
        // Parse hash fragment for auth tokens
        const hash = window.location.hash.substring(1); // Remove the #
        const hashParams = new URLSearchParams(hash);
        const accessToken = hashParams.get('access_token');
        
        if (!accessToken) {
          console.log('❌ [InvitationPage] No access token found');
          setError('Invalid invitation link - missing authentication');
          setLoading(false);
          return;
        }
        
        // Validate the invitation token via API
        console.log('🔄 [InvitationPage] Calling validation API');
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/organizations/invitations/validate?token=${encodeURIComponent(invitationToken)}`);
        
        if (!response.ok) {
          console.error('❌ [InvitationPage] Validation API failed:', response.status);
          setError('Failed to validate invitation - please try again');
          setLoading(false);
          return;
        }
        
        const validationResult = await response.json();

        if (!validationResult.valid || !validationResult.invitation) {
          console.log('❌ [InvitationPage] Invalid invitation token');
          setError('Invalid or expired invitation link');
          setLoading(false);
          return;
        }

        const invitation = validationResult.invitation;
        const userExists = validationResult.user_exists || false;

        console.log('✅ [InvitationPage] Valid invitation found:', {
          email: invitation.email,
          orgId: invitation.organization_id,
          role: invitation.role,
          userExists
        });

        setUserAlreadyExists(userExists);
        setInvitationData({
          email: invitation.email,
          orgId: invitation.organization_id,
          role: invitation.role,
          accessToken
        });
        setLoading(false);
        
      } catch (error) {
        console.error('❌ [InvitationPage] Error validating invitation token:', error);
        setError('Failed to validate invitation - please check your connection');
        setLoading(false);
      }
    };

    validateInvitationToken();
  }, [searchParams]);

  const handleAcceptInvitation = async () => {
    if (!invitationData) {
      toast({
        title: "Error",
        description: "Invalid invitation data.",
        variant: "destructive"
      });
      return;
    }

    try {
      setAccepting(true);
      
      console.log('🔄 [InvitationPage] Accepting invitation via Supabase API');
      const organization = await acceptInvitationSupabase(
        invitationData.email,
        invitationData.orgId,
        invitationData.role,
        invitationData.accessToken
      );
      
      console.log('✅ [InvitationPage] Invitation accepted successfully');
      setSuccess(true);
      
      // Refresh organizations in context
      await refreshOrganizations();
      
      // Switch to the newly joined organization
      await switchOrganization(invitationData.orgId);
      
      // Different flow for new vs existing users
      if (userAlreadyExists) {
        // Existing user - redirect to login
        toast({
          title: "Welcome to the team!",
          description: `You've successfully joined ${organization.name}. Please sign in to continue.`,
        });

        setTimeout(() => {
          navigate(`/login?email=${encodeURIComponent(invitationData.email)}`);
        }, 2000);
      } else {
        // New user - redirect to set password
        toast({
          title: "Welcome to the team!",
          description: `You've successfully joined ${organization.name}. Please set up your password.`,
        });

        setTimeout(() => {
          // Parse hash fragment for auth tokens to pass them along
          const hash = window.location.hash.substring(1);
          const hashParams = new URLSearchParams(hash);
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');

          navigate('/set-password', {
            state: {
              email: invitationData.email,
              organizationName: organization.name,
              fromInvitation: true,
              accessToken: accessToken,
              refreshToken: refreshToken
            }
          });
        }, 2000);
      }
      
    } catch (error) {
      console.error('❌ [InvitationPage] Error accepting invitation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to accept invitation';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Processing Invitation | Hobbes</title>
        </Helmet>
        {/* Fixed Favicon in Top-Left */}
        <motion.div
          variants={loginLogo}
          initial="initial"
          animate="animate"
          className="fixed top-6 left-6 z-10 gpu-accelerated"
        >
          <img src={hobbesFavicon} alt="Hobbes" className="h-8 w-8" />
        </motion.div>

        <div className="grid min-h-svh lg:grid-cols-2">
          {/* Left Column: Loading Form */}
          <div className="flex items-center justify-center p-6 md:p-10">
            <motion.div 
              variants={loginFadeIn}
              initial="initial"
              animate="animate"
              className="w-full max-w-sm login-performance"
            >
              <Card>
                <CardHeader>
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-72" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24 mt-1" />
                    </div>
                  </div>
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Right Column: Cover Image */}
          <AuthRightPanel />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Invalid Invitation | Hobbes</title>
        </Helmet>
        {/* Fixed Favicon in Top-Left */}
        <motion.div
          variants={loginLogo}
          initial="initial"
          animate="animate"
          className="fixed top-6 left-6 z-10 gpu-accelerated"
        >
          <img src={hobbesFavicon} alt="Hobbes" className="h-8 w-8" />
        </motion.div>

        <div className="grid min-h-svh lg:grid-cols-2">
          {/* Left Column: Error Message */}
          <div className="flex items-center justify-center p-6 md:p-10">
            <motion.div 
              variants={loginFadeIn}
              initial="initial"
              animate="animate"
              className="w-full max-w-sm login-performance"
            >
              <motion.div
                variants={loginFormStagger}
                initial="initial"
                animate="animate"
                className="form-performance"
              >
                <Card>
                  <CardHeader>
                    <motion.div variants={loginFormItem}>
                      <CardTitle className="flex items-center gap-2 smooth-animation optimize-text">
                        <XCircle className="h-5 w-5 text-red-500" />
                        Invalid Invitation
                      </CardTitle>
                      <CardDescription>
                        {error}
                      </CardDescription>
                    </motion.div>
                  </CardHeader>
                  <CardContent>
                    <motion.div variants={loginFormItem} className="smooth-animation">
                      <motion.div
                        variants={loginButton}
                        whileHover="hover"
                        whileTap="tap"
                        className="button-performance"
                      >
                        <Button 
                          variant="outline" 
                          onClick={() => navigate('/')} 
                          className="w-full force-gpu"
                        >
                          Go Home
                        </Button>
                      </motion.div>
                    </motion.div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </div>

          {/* Right Column: Cover Image */}
          <AuthRightPanel />
        </div>
      </>
    );
  }

  if (success) {
    return (
      <>
        <Helmet>
          <title>Welcome! | Hobbes</title>
        </Helmet>
        {/* Fixed Favicon in Top-Left */}
        <motion.div
          variants={loginLogo}
          initial="initial"
          animate="animate"
          className="fixed top-6 left-6 z-10 gpu-accelerated"
        >
          <img src={hobbesFavicon} alt="Hobbes" className="h-8 w-8" />
        </motion.div>

        <div className="grid min-h-svh lg:grid-cols-2">
          {/* Left Column: Success Message */}
          <div className="flex items-center justify-center p-6 md:p-10">
            <motion.div 
              variants={loginFadeIn}
              initial="initial"
              animate="animate"
              className="w-full max-w-sm login-performance"
            >
              <motion.div
                variants={loginFormStagger}
                initial="initial"
                animate="animate"
                className="form-performance"
              >
                <Card>
                  <CardHeader>
                    <motion.div variants={loginFormItem}>
                      <CardTitle className="flex items-center gap-2 smooth-animation optimize-text">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        Welcome to the team!
                      </CardTitle>
                      <CardDescription>
                        Your invitation has been accepted successfully. You're being redirected...
                      </CardDescription>
                    </motion.div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <motion.div variants={loginFormItem} className="smooth-animation">
                      <div className="flex items-center gap-3 p-3 border rounded-lg bg-green-50 border-green-200">
                        <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-green-800">Invitation Accepted</h3>
                          <p className="text-sm text-green-600">You're now part of the organization</p>
                        </div>
                      </div>
                    </motion.div>
                    
                    <motion.div variants={loginFormItem} className="smooth-animation">
                      <div className="text-center">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground inline-block" />
                        <p className="text-sm text-muted-foreground mt-2">Redirecting...</p>
                      </div>
                    </motion.div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </div>

          {/* Right Column: Cover Image */}
          <AuthRightPanel />
        </div>
      </>
    );
  }

  if (!invitationData) {
    return (
      <>
        <Helmet>
          <title>Invitation Not Found | Hobbes</title>
        </Helmet>
        {/* Fixed Favicon in Top-Left */}
        <motion.div
          variants={loginLogo}
          initial="initial"
          animate="animate"
          className="fixed top-6 left-6 z-10 gpu-accelerated"
        >
          <img src={hobbesFavicon} alt="Hobbes" className="h-8 w-8" />
        </motion.div>

        <div className="grid min-h-svh lg:grid-cols-2">
          {/* Left Column: Not Found Message */}
          <div className="flex items-center justify-center p-6 md:p-10">
            <motion.div 
              variants={loginFadeIn}
              initial="initial"
              animate="animate"
              className="w-full max-w-sm login-performance"
            >
              <motion.div
                variants={loginFormStagger}
                initial="initial"
                animate="animate"
                className="form-performance"
              >
                <Card>
                  <CardHeader>
                    <motion.div variants={loginFormItem}>
                      <CardTitle className="flex items-center gap-2 smooth-animation optimize-text">
                        <XCircle className="h-5 w-5 text-red-500" />
                        Invitation Not Found
                      </CardTitle>
                      <CardDescription>
                        This invitation link is invalid or has expired.
                      </CardDescription>
                    </motion.div>
                  </CardHeader>
                  <CardContent>
                    <motion.div variants={loginFormItem} className="smooth-animation">
                      <motion.div
                        variants={loginButton}
                        whileHover="hover"
                        whileTap="tap"
                        className="button-performance"
                      >
                        <Button 
                          variant="outline" 
                          onClick={() => navigate('/')} 
                          className="w-full force-gpu"
                        >
                          Go Home
                        </Button>
                      </motion.div>
                    </motion.div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </div>

          {/* Right Column: Cover Image */}
          <AuthRightPanel />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>You're Invited! | Hobbes</title>
      </Helmet>
      {/* Fixed Favicon in Top-Left */}
      <motion.div
        variants={loginLogo}
        initial="initial"
        animate="animate"
        className="fixed top-6 left-6 z-10 gpu-accelerated"
      >
        <img src={hobbesFavicon} alt="Hobbes" className="h-8 w-8" />
      </motion.div>

      <div className="grid min-h-svh lg:grid-cols-2">
        {/* Left Column: Invitation Form */}
        <div className="flex items-center justify-center p-6 md:p-10">
          <motion.div 
            variants={loginFadeIn}
            initial="initial"
            animate="animate"
            className="w-full max-w-sm login-performance"
          >
            <motion.div
              variants={loginFormStagger}
              initial="initial"
              animate="animate"
              className="form-performance"
            >
              <Card>
                <CardHeader>
                  <motion.div variants={loginFormItem}>
                    <CardTitle className="flex items-center gap-2 smooth-animation optimize-text">
                      <Building2 className="h-5 w-5" />
                      You're Invited!
                    </CardTitle>
                    <CardDescription>
                      You've been invited to join an organization.
                    </CardDescription>
                  </motion.div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Invitation Details */}
                  <motion.div variants={loginFormItem} className="smooth-animation">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>Invited as:</span>
                        <Badge variant={invitationData.role === 'admin' ? 'default' : 'secondary'}>
                          {invitationData.role === 'admin' ? (
                            <>
                              <Crown className="h-3 w-3 mr-1" />
                              Admin
                            </>
                          ) : (
                            <>
                              <User className="h-3 w-3 mr-1" />
                              Member
                            </>
                          )}
                        </Badge>
                      </div>

                      <div className="text-sm text-muted-foreground">
                        <p>Email: <strong>{invitationData.email}</strong></p>
                      </div>

                      <div className="text-sm text-muted-foreground">
                        <p>
                          {invitationData.role === 'admin' 
                            ? 'Admins can manage organization settings and invite other members.'
                            : 'Members can access organization data and create content.'
                          }
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Action Buttons */}
                  <motion.div variants={loginFormItem} className="smooth-animation">
                    <div className="flex gap-3">
                      <motion.div
                        variants={loginButton}
                        whileHover="hover"
                        whileTap="tap"
                        className="button-performance flex-1"
                      >
                        <Button 
                          onClick={handleAcceptInvitation}
                          disabled={accepting}
                          className="flex-1 force-gpu"
                        >
                          {accepting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Joining...
                            </>
                          ) : (
                            'Accept Invitation'
                          )}
                        </Button>
                      </motion.div>
                      <motion.div
                        variants={loginButton}
                        whileHover="hover"
                        whileTap="tap"
                        className="button-performance"
                      >
                        <Button 
                          variant="outline" 
                          onClick={handleDecline}
                          disabled={accepting}
                          className="force-gpu"
                        >
                          Decline
                        </Button>
                      </motion.div>
                    </div>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>

        {/* Right Column: Cover Image */}
        <AuthRightPanel />
      </div>
    </>
  );
};

export default InvitationPage;
